import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { 
  Calendar, 
  Loader2, 
  AlertCircle, 
  Download, 
  Archive, 
  Trash2, 
  Clock, 
  CheckCircle, 
  ChevronDown, 
  ChevronUp,
  Share2,
  Mail,
  Globe,
  Copy,
  X,
  Rss,
  List,
  Edit
} from 'lucide-react';
import { format, parseISO, isToday } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useSermons } from '../../hooks/useSermons';
import { downloadICS, generateGoogleCalendarURL, generateOutlookURL } from '../../lib/calendar';

interface SermonSchedule {
  id: string;
  title: string;
  date: string;
  scripture: string | null;
  notes: string | null;
  series_id: string | null;
  status: 'planned' | 'in-progress' | 'completed';
  series?: {
    title: string;
  };
  sermon_id: string | null;
}

interface SharedSchedule {
  id: string;
  title: string;
  description: string | null;
  expires_at: string;
}

export function SermonScheduler() {
  const navigate = useNavigate();
  const { series } = useSermons();
  const [schedules, setSchedules] = useState<SermonSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<SermonSchedule | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [isSharing, setIsSharing] = useState(false);
  const [shareTitle, setShareTitle] = useState('');
  const [shareDescription, setShareDescription] = useState('');
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    loadSchedules();
  }, [currentMonth]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      setError(null);

      const start = format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1), 'yyyy-MM-dd');
      const end = format(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0), 'yyyy-MM-dd');

      const { data, error: fetchError } = await supabase
        .from('sermon_schedules')
        .select('*, series(title)')
        .gte('date', start)
        .lte('date', end)
        .order('date');

      if (fetchError) throw fetchError;
      setSchedules(data || []);
    } catch (e) {
      console.error('Error loading schedules:', e);
      setError(e instanceof Error ? e.message : 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSchedule = (schedule: SermonSchedule) => {
    setEditingSchedule(schedule);
  };

  const handleUpdateSchedule = async () => {
    if (!editingSchedule) return;

    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update schedule
      const { error: updateError } = await supabase
        .from('sermon_schedules')
        .update({
          notes: editingSchedule.notes || null,
          date: editingSchedule.date // Ensure date is included in update
        })
        .eq('id', editingSchedule.id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      await loadSchedules();
      setEditingSchedule(null);
    } catch (e) {
      console.error('Error updating schedule:', e);
      setError(e instanceof Error ? e.message : 'Failed to update schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;

    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('sermon_schedules')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await loadSchedules();
    } catch (e) {
      console.error('Error deleting schedule:', e);
      setError(e instanceof Error ? e.message : 'Failed to delete schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCalendar = () => {
    downloadICS(schedules);
  };

  const handleAddToGoogleCalendar = (schedule: SermonSchedule) => {
    window.open(generateGoogleCalendarURL(schedule), '_blank');
  };

  const handleAddToOutlook = (schedule: SermonSchedule) => {
    window.open(generateOutlookURL(schedule), '_blank');
  };

  const handleShare = async (schedule: SermonSchedule) => {
    try {
      const { data, error } = await supabase
        .from('shared_schedules')
        .insert([{
          title: schedule.title,
          description: `${schedule.scripture || ''}\n\n${schedule.notes || ''}`,
          user_id: (await supabase.auth.getUser()).data.user?.id
        }])
        .select()
        .single();

      if (error) throw error;

      const shareUrl = `${window.location.origin}/shared-schedule/${data.id}`;
      await navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard!');
    } catch (err) {
      console.error('Error sharing schedule:', err);
      setError('Failed to create share link');
    }
  };

  const handleGetCalendarFeed = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Build feed URL manually now
      const feedUrl = `https://qoxdevzrzqcqbtdehgwn.functions.supabase.co/calendar-feed?userId=${user.id}`;
      setFeedUrl(feedUrl);
      
      // Create a dialog to display the feed URL
      const dialog = document.createElement('div');
      dialog.className = "fixed inset-0 bg-black/50 flex items-center justify-center z-50";
      dialog.innerHTML = `
        <div class="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full mx-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-midnight">Calendar Feed URL</h3>
            <button class="text-slate hover:text-midnight close-dialog">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <p class="text-sm text-slate mb-4">
            Use this URL to subscribe to your sermon schedule in your calendar application:
          </p>
          <div class="flex items-center space-x-2">
            <input
              type="text"
              value="${feedUrl}"
              readonly
              class="flex-1 px-3 py-2 border border-sand rounded-lg bg-sand/10"
            />
            <button class="copy-url px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90">
              Copy URL
            </button>
          </div>
          <div class="mt-4 text-sm text-slate">
            <p class="font-medium mb-2">Instructions:</p>
            <ol class="list-decimal pl-4 space-y-1">
              <li>Copy the URL above</li>
              <li>In your calendar app, look for an option to "Subscribe to Calendar" or "Add Calendar Subscription"</li>
              <li>Paste the URL when prompted</li>
              <li>Set the refresh interval as desired (recommended: daily)</li>
            </ol>
            <p class="mt-2 text-xs text-crimson">Note: If you have trouble with Outlook, try downloading the .ics file instead and importing it manually.</p>
          </div>
        </div>
      `;

      document.body.appendChild(dialog);

      const closeBtn = dialog.querySelector('.close-dialog');
      closeBtn?.addEventListener('click', () => {
        document.body.removeChild(dialog);
      });

      const copyBtn = dialog.querySelector('.copy-url');
      copyBtn?.addEventListener('click', async () => {
        await navigator.clipboard.writeText(feedUrl);
        setCopySuccess(true);
        const btn = copyBtn as HTMLButtonElement;
        btn.textContent = 'Copied!';
        btn.classList.add('bg-emerald');
        btn.classList.remove('bg-electric');
        setTimeout(() => {
          btn.textContent = 'Copy URL';
          btn.classList.remove('bg-emerald');
          btn.classList.add('bg-electric');
          setCopySuccess(false);
        }, 2000);
      });
    } catch (err) {
      console.error('Error getting calendar feed:', err);
      setError('Failed to generate calendar feed URL');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-crimson/10 text-crimson rounded-lg flex items-center">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Calendar className="h-5 w-5 text-midnight" />
          <h2 className="text-xl font-semibold text-midnight">Sermon Schedule</h2>
        </div>
        <div className="flex items-center space-x-4">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="flex items-center px-4 py-2 bg-sand/20 text-midnight rounded-lg hover:bg-sand/30">
                <Calendar className="h-4 w-4 mr-2" />
                Sync Calendar
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="min-w-[220px] bg-white rounded-lg shadow-lg border border-sand/20 p-2 z-50"
                sideOffset={5}
                align="end"
              >
                <DropdownMenu.Item
                  className="flex items-center px-3 py-2 text-sm text-midnight hover:bg-sand/10 rounded-md cursor-pointer"
                  onSelect={handleExportCalendar}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Calendar (.ics)
                </DropdownMenu.Item>
                
                <DropdownMenu.Item
                  className="flex items-center px-3 py-2 text-sm text-midnight hover:bg-sand/10 rounded-md cursor-pointer"
                  onSelect={handleGetCalendarFeed}
                >
                  <Rss className="h-4 w-4 mr-2" />
                  Subscribe to Calendar Feed
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>

          <button
            onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')}
            className="flex items-center px-4 py-2 bg-sand/20 text-midnight rounded-lg hover:bg-sand/30"
          >
            {viewMode === 'calendar' ? (
              <>
                <List className="h-4 w-4 mr-2" />
                List View
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4 mr-2" />
                Calendar View
              </>
            )}
          </button>
        </div>
      </div>

      {editingSchedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-midnight">Edit Schedule</h3>
              <button
                onClick={() => setEditingSchedule(null)}
                className="text-slate hover:text-midnight"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-1">Date</label>
                <input
                  type="date"
                  value={editingSchedule.date}
                  onChange={(e) => setEditingSchedule({ ...editingSchedule, date: e.target.value })}
                  className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-1">Notes</label>
                <textarea
                  value={editingSchedule.notes || ''}
                  onChange={(e) => setEditingSchedule({ ...editingSchedule, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                  placeholder="Add production notes, reminders, etc..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingSchedule(null)}
                  className="px-4 py-2 text-slate hover:text-midnight"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdateSchedule}
                  className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'calendar' ? (
        <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-midnight">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
                className="p-2 hover:bg-sand/20 rounded-lg"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentMonth(new Date())}
                className="px-4 py-2 bg-sand/20 rounded-lg hover:bg-sand/30"
              >
                Today
              </button>
              <button
                onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
                className="p-2 hover:bg-sand/20 rounded-lg"
              >
                Next
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-slate">
                {day}
              </div>
            ))}
            {Array.from({ length: 42 }, (_, i) => {
              const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i - (new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay()) + 1);
              const dateStr = format(date, 'yyyy-MM-dd');
              const daySchedules = schedules.filter(s => s.date === dateStr);
              const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
              const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
              const isCurrentDay = isToday(date);

              return (
                <div
                  key={dateStr}
                  className={`p-2 min-h-[100px] rounded-lg border ${
                    isCurrentDay
                      ? 'bg-electric/10 border-electric'
                      : isPast
                      ? 'bg-sand/5 border-sand/20'
                      : isCurrentMonth
                      ? 'bg-white border-sand/20 hover:border-electric/20'
                      : 'bg-sand/5 border-sand/20 opacity-50'
                  }`}
                >
                  <div className="text-sm font-medium mb-2">
                    {format(date, 'd')}
                  </div>
                  {daySchedules.map(schedule => (
                    <div
                      key={schedule.id}
                      className="mb-2 p-2 bg-white rounded border border-sand/20 text-sm cursor-pointer hover:border-electric"
                      onClick={() => handleEditSchedule(schedule)}
                    >
                      <div className="font-medium text-midnight truncate">
                        {schedule.title}
                      </div>
                      {schedule.scripture && (
                        <div className="text-xs text-slate truncate">
                          {schedule.scripture}
                        </div>
                      )}
                      {schedule.series?.title && (
                        <div className="text-xs text-electric truncate">
                          {schedule.series.title}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {schedules.map(schedule => (
            <div
              key={schedule.id}
              className="bg-white rounded-lg shadow-sm border border-sand/20 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-midnight">
                    {schedule.title}
                  </h3>
                  <div className="text-sm text-slate">
                    {format(parseISO(schedule.date), 'MMMM d, yyyy')}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditSchedule(schedule)}
                    className="p-2 hover:bg-sand/20 rounded-lg"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteSchedule(schedule.id)}
                    className="p-2 hover:bg-crimson/10 text-crimson rounded-lg"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {schedule.scripture && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-slate mb-1">
                    Scripture
                  </div>
                  <div className="text-midnight">{schedule.scripture}</div>
                </div>
              )}

              {schedule.notes && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-slate mb-1">
                    Notes
                  </div>
                  <div className="text-midnight whitespace-pre-wrap">
                    {schedule.notes}
                  </div>
                </div>
              )}

              {schedule.series?.title && (
                <div className="flex items-center text-sm text-electric">
                  <Calendar className="h-4 w-4 mr-1" />
                  {schedule.series.title}
                </div>
              )}

              <div className="mt-4 flex items-center space-x-4">
                <button
                  onClick={() => handleAddToGoogleCalendar(schedule)}
                  className="flex items-center text-sm text-slate hover:text-midnight"
                >
                  <Globe className="h-4 w-4 mr-1" />
                  Add to Google Calendar
                </button>
                <button
                  onClick={() => handleAddToOutlook(schedule)}
                  className="flex items-center text-sm text-slate hover:text-midnight"
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Add to Outlook
                </button>
                <button
                  onClick={() => handleShare(schedule)}
                  className="flex items-center text-sm text-slate hover:text-midnight"
                >
                  <Share2 className="h-4 w-4 mr-1" />
                  Share Schedule
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}